// Utilidades para manejar planes y funcionalidades
export const PRODUCT_PLAN_SLUGS = ['basico', 'profesional', 'empresarial'];

export const filterProductPlans = (plans = []) =>
  [...plans]
    .filter((p) => PRODUCT_PLAN_SLUGS.includes((p.slug || '').toLowerCase()) && p.isActive !== false)
    .sort((a, b) => (Number(a.price) || 0) - (Number(b.price) || 0));

export const isPlanPopular = (plan) =>
  Boolean(plan?.isPopular || (plan?.slug || '').toLowerCase() === 'profesional');

const PLAN_NAMES = {
  basico: 'Básico',
  profesional: 'Profesional',
  empresarial: 'Empresarial',
};

export function getUserPlanInfo(user) {
  const plan = user?.currentPlan;
  if (plan?.name) {
    const slug = (plan.slug || plan.name || 'basico').toLowerCase();
    return {
      name: plan.name,
      slug,
      tagline: getPlanTagline(plan),
    };
  }

  const features = user?.unlockedFeatures || {};
  if (features.multiCompany || features.employeeAccounts) {
    return {
      name: PLAN_NAMES.empresarial,
      slug: 'empresarial',
      tagline: getPlanTagline({ slug: 'empresarial' }),
    };
  }
  if (features.multimoneda || features.advancedAnalytics || features.flujoDeCaja) {
    return {
      name: PLAN_NAMES.profesional,
      slug: 'profesional',
      tagline: getPlanTagline({ slug: 'profesional' }),
    };
  }

  return {
    name: PLAN_NAMES.basico,
    slug: 'basico',
    tagline: getPlanTagline({ slug: 'basico' }),
  };
}

export function getPlanBadgeClasses(slug, modoOscuro = false) {
  const s = (slug || 'basico').toLowerCase();
  const styles = {
    basico: modoOscuro
      ? 'bg-gray-800 text-gray-200 border-gray-600'
      : 'bg-gray-100 text-gray-700 border-gray-200',
    profesional: modoOscuro
      ? 'bg-blue-950/60 text-blue-200 border-blue-800'
      : 'bg-blue-50 text-blue-800 border-blue-200',
    empresarial: modoOscuro
      ? 'bg-purple-950/60 text-purple-200 border-purple-800'
      : 'bg-purple-50 text-purple-800 border-purple-200',
  };
  return styles[s] || styles.basico;
}

export const getPlanTagline = (plan) => {
  const slug = (plan?.slug || '').toLowerCase();
  if (slug === 'basico') {
    return 'Cobrar con WhatsApp, resumen básico en Reportes y bandeja del día. 30 días gratis.';
  }
  if (slug === 'profesional') {
    return 'Análisis de cobranza completo, alertas DSO, margen por cliente e invitar contador Lector.';
  }
  if (slug === 'empresarial') {
    return 'Todo Profesional + equipo, auditoría, multi-empresa y presupuestos con alertas.';
  }
  return plan?.description || '';
};

export const getPlanBulletPoints = (plan) => {
  const slug = (plan?.slug || '').toLowerCase();
  if (slug === 'basico') {
    const maxCli = plan?.maxClients ?? 20;
    const maxDoc = plan?.maxDocumentsPerMonth ?? 50;
    return [
      'Ventas, gastos y movimientos que se repiten solos',
      'Cobrar: impagas, WhatsApp, gestiones y pagos parciales',
      'Resumen básico de cobranzas y calendario de vencimientos',
      `Hasta ${maxCli} clientes y ${maxDoc} ventas/gastos por mes · 30 días gratis`,
    ];
  }
  if (slug === 'profesional') {
    return [
      '40 escaneos OCR/mes (comprobante → egreso) + packs extra opcionales',
      'Análisis de cobranza: tendencias, DSO, export CSV y widget en Inicio',
      'Margen real por cliente (egresos asignados) y margen estimado',
      'Cobrar con link Mercado Pago, score y secuencias automáticas',
      'Vista contador, invitar contador Lector y pack contable automático',
      'Estado de resultados, multimoneda e import CSV de egresos',
      'Reporte mensual por email (opt-in en Cuenta)',
    ];
  }
  if (slug === 'empresarial') {
    return [
      '150 escaneos OCR/mes + packs extra opcionales',
      'Todo Profesional + hasta 10 usuarios con auditoría',
      'Multi-empresa: hasta 5 negocios en una cuenta',
      'Presupuestos por categoría con alertas de desvío',
    ];
  }
  return [];
};

/** Resumen corto para registro (3 ítems; detalle en /planes). */
export function getPlanSignupBulletPoints(plan) {
  const slug = (plan?.slug || '').toLowerCase();
  if (slug === 'basico') {
    const maxCli = plan?.maxClients ?? 20;
    const maxDoc = plan?.maxDocumentsPerMonth ?? 50;
    return [
      'Ventas, gastos y cobro con WhatsApp',
      `Hasta ${maxCli} clientes · ${maxDoc} movimientos/mes`,
      '30 días gratis · 3 OCR de prueba',
    ];
  }
  if (slug === 'profesional') {
    return [
      'Todo Básico + 40 escaneos OCR/mes',
      'DSO, link MP, secuencias y recordatorios',
      'Estado de resultados, multimoneda y pack contador',
    ];
  }
  if (slug === 'empresarial') {
    return [
      'Todo Profesional + 150 OCR/mes',
      'Hasta 10 usuarios y multi-empresa (5 negocios)',
      'Presupuestos por categoría con alertas',
    ];
  }
  return getPlanBulletPoints(plan).slice(0, 3);
};

/** Precio mensual canónico: columna `price` (ARS). Ignora monthlyPrice legacy (USD). */
export const getPlanMonthlyPrice = (plan) => {
  const base = Number(plan?.price);
  if (Number.isFinite(base) && base > 0) return base;
  const monthly = Number(plan?.monthlyPrice);
  if (Number.isFinite(monthly) && monthly > 0) return monthly;
  return 0;
};

/** Monto a cobrar según ciclo (anual = 10 meses, mismo criterio que backend). */
export function getPlanBillingAmount(plan, billingCycle = 'monthly') {
  const monthly = getPlanMonthlyPrice(plan);
  if (billingCycle === 'annual') {
    const annual = Number(plan?.annualPrice);
    if (Number.isFinite(annual) && annual >= monthly) return annual;
    return monthly * 10;
  }
  return monthly;
}

export function formatPlanPrice(plan, billingCycle = 'monthly') {
  const amount = getPlanBillingAmount(plan, billingCycle);
  const currency = plan?.currency || 'ARS';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

export function getPlanBillingPeriodLabel(billingCycle) {
  return billingCycle === 'annual' ? 'año' : 'mes';
}

